/**
 * The seam between the agent's command modules and the copilot provider.
 *
 * Navigation, requests and the envelope executor are module code, not React:
 * they run from a room event, from a timer, from the dev console. What they need
 * from the provider — the store, the silent report channel, the message list,
 * the confirmation card — is handed over here once, when the provider mounts.
 *
 * `agentRuntime()` never returns null. Before the provider mounts, after it
 * unmounts, and in a check script there is no conversation to write to, and a
 * caller that had to test for that at every step would eventually forget one.
 * The no-op answers every call the way "nobody is listening" should read:
 * reports and confirmations come back false, messages get no id.
 *
 * The returned object is a facade that looks the provider's runtime up on every
 * call rather than a copy of it. A request that awaits a confirmation for two
 * minutes holds on to what `agentRuntime()` gave it; if the provider was torn
 * down meanwhile (sign-out), the later report must fall silent instead of going
 * to a runtime whose room is gone.
 */

import { agentSingleton } from '../../lib/agent/singleton';

/** Stands in for the store when no provider is mounted: reads as empty, dispatches nowhere. */
const STUB_STORE = Object.freeze({
  getState: () => ({}),
  dispatch: (action) => action,
  subscribe: () => () => {},
});

/**
 * The installed runtime, in a box so every instance of this module shares it.
 * A bare `let` would be one binding per module instance, and the half the
 * provider installed into would not be the half the executor reports through
 * — see `lib/agent/singleton.js`.
 */
const box = agentSingleton('agentRuntime', () => ({ current: null }));

/**
 * Where the browser is, for callers with no router to ask. The provider's own
 * `currentLocation` is preferred; this only answers when it is absent.
 */
function windowLocation() {
  if (typeof window === 'undefined' || !window.location) return { pathname: '', search: '' };
  return { pathname: window.location.pathname, search: window.location.search };
}

/**
 * Call a method on the installed runtime, falling back when it is missing or
 * throws. A provider bug must not surface as an exception inside the executor,
 * which would then try to report that exception through the same broken path.
 */
function invoke(method, args, fallback) {
  const runtime = box.current;
  const fn = runtime && typeof runtime[method] === 'function' ? runtime[method] : null;
  if (!fn) return fallback();
  try {
    return fn.apply(runtime, args);
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[agent] runtime.${method} threw`, err);
    }
    return fallback();
  }
}

/** Promise-returning methods also resolve to the fallback when they reject. */
function invokeAsync(method, args, fallbackValue) {
  const settle = (value) => (typeof value === 'boolean' ? value : Boolean(value));
  const result = invoke(method, args, () => fallbackValue);
  return Promise.resolve(result).then(settle, (err) => {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[agent] runtime.${method} rejected`, err);
    }
    return fallbackValue;
  });
}

const facade = Object.freeze({
  /** The redux store; a stub that reads as empty when no provider is mounted. */
  get store() {
    return (box.current && box.current.store) || STUB_STORE;
  },
  /** Silent `[api_result]` send. Resolves true once delivered, false otherwise. */
  report(text) {
    return invokeAsync('report', [text], false);
  },
  /** Adds a line to the provider's message list and returns its id, or null. */
  addMessage(msg) {
    const id = invoke('addMessage', [msg], () => null);
    return id == null ? null : id;
  },
  /** Patches a line already in the list; silently nothing when it is gone. */
  updateMessage(id, patch) {
    if (id == null) return;
    invoke('updateMessage', [id, patch], () => undefined);
  },
  /**
   * Resolves true only when the user pressed Run on the confirmation card,
   * 'expired' when the card was left unanswered until it expired, and false
   * otherwise (Cancel, no provider, a teardown).
   */
  confirm(request) {
    const result = invoke('confirm', [request], () => false);
    return Promise.resolve(result).then(
      (answer) => (answer === true || answer === 'expired' ? answer : false),
      (err) => {
        if (process.env.NODE_ENV !== 'production') console.warn('[agent] runtime.confirm rejected', err);
        return false;
      },
    );
  },
  /** `{ pathname, search }` of what the user is looking at. */
  currentLocation() {
    const loc = invoke('currentLocation', [], windowLocation);
    if (!loc || typeof loc.pathname !== 'string') return windowLocation();
    return { pathname: loc.pathname, search: typeof loc.search === 'string' ? loc.search : '' };
  },
});

/**
 * Install the provider's runtime, or `null` to remove it.
 *
 * Returns an uninstall function that only clears the runtime if it is still the
 * one this call installed. A remount (React StrictMode, a provider keyed on the
 * account) can install the next runtime before the previous cleanup runs, and a
 * blind `setAgentRuntime(null)` from that cleanup would disconnect the new one.
 */
export function setAgentRuntime(runtime) {
  const installed = runtime || null;
  box.current = installed;
  return () => {
    if (box.current === installed) box.current = null;
  };
}

/** The runtime facade. Never null; see the module comment. */
export function agentRuntime() {
  return facade;
}

/** Whether a provider has installed a runtime — for devtools and diagnostics only. */
export function hasAgentRuntime() {
  return box.current != null;
}
